import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, BarChart3, Loader2 } from 'lucide-react';
import axios from '../lib/axios';

// Modal de resumen agregado de un lote de liquidación/decomisión (por job_id)
const JobResumenModal = ({ isOpen, onClose, jobId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['job-resumen', jobId],
    queryFn: async () => {
      const res = await axios.get(`/api/jobs/${jobId}/resumen`);
      return res.data;
    },
    enabled: isOpen && !!jobId
  });

  if (!isOpen) return null;

  const resumen = data?.data;

  const formatMoney = (num) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-white" />
              <h3 className="text-lg font-semibold text-white">Resumen del lote</h3>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Calculando resumen...</span>
              </div>
            ) : isError || !resumen ? (
              <div className="text-center py-12 text-red-600">Error obteniendo el resumen del lote</div>
            ) : (
              <div className="space-y-6">
                {/* Totales */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <StatBox label="Total registros" value={resumen.totales.total} />
                  <StatBox label="Éxito" value={resumen.totales.exitosos} color="green" />
                  <StatBox label="Error" value={resumen.totales.errores} color="red" />
                  <StatBox label="Pendiente" value={resumen.totales.pendientes} color="amber" />
                  <StatBox label="Monto total" value={formatMoney(resumen.totales.monto_total)} color="blue" />
                </div>

                <DesgloseTabla titulo="Por canal" filas={resumen.por_canal} claveLabel="Canal" formatMoney={formatMoney} />
                <DesgloseTabla titulo="Por tarifa ATR" filas={resumen.por_tarifa} claveLabel="Tarifa ATR" formatMoney={formatMoney} />
                <DesgloseTabla titulo="Por esquema de liquidación" filas={resumen.por_esquema_liquidacion} claveLabel="Esquema" formatMoney={formatMoney} />

                {resumen.por_decomision_estado && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Estado de decomisión de estas liquidaciones</h4>
                    <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-medium text-gray-600">Decomisionado</th>
                          <th className="px-3 py-1.5 text-right font-medium text-gray-600">Registros</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {resumen.por_decomision_estado.map((fila) => (
                          <tr key={fila.clave ?? 'null'}>
                            <td className="px-3 py-1.5 text-gray-800">{fila.clave || 'sin decomisión'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-900">{fila.total.toLocaleString('es-ES')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, color = 'gray' }) => {
  const colorClasses = {
    gray: 'bg-gray-50 border-gray-200 text-gray-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900'
  };
  return (
    <div className={`border rounded-lg p-3 ${colorClasses[color]}`}>
      <p className="text-xs font-medium opacity-75 mb-1">{label}</p>
      <p className="text-lg font-bold">{typeof value === 'number' ? value.toLocaleString('es-ES') : value}</p>
    </div>
  );
};

const DesgloseTabla = ({ titulo, filas, claveLabel, formatMoney }) => {
  if (!filas || filas.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-2">{titulo}</h4>
      <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-1.5 text-left font-medium text-gray-600">{claveLabel}</th>
            <th className="px-3 py-1.5 text-right font-medium text-gray-600">Registros</th>
            <th className="px-3 py-1.5 text-right font-medium text-gray-600">Monto total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {filas.map((fila) => (
            <tr key={fila.clave ?? 'null'}>
              <td className="px-3 py-1.5 text-gray-800">{fila.clave || '-'}</td>
              <td className="px-3 py-1.5 text-right text-gray-900">{fila.total.toLocaleString('es-ES')}</td>
              <td className="px-3 py-1.5 text-right text-gray-900">{formatMoney(fila.monto_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default JobResumenModal;
